// @ts-strict-ignore
import {StateController} from 'panel';
import debounce from 'lodash/debounce';

import {reporter} from 'common/report/error-reporting';
import {Subscribable} from 'common/types/context/subscribable';
import {LocalizationStore} from 'common/i18n/store';

import {Store} from '../stores/store';
import {DashboardsMap} from '../stores/dashboards';
import {Dashboard} from '../dashboards/types';
import {RCASourceData} from '../queries/app-api/dashboards-client';
import {DashboardModelController} from './dashboard-model-controller';
import {DashboardsModelClient, QueryAllDashboardType} from './types';

type DashboardSelectorControllerState<CustomSelectorClient extends DashboardsModelClient> = {
  activeDashboardController: DashboardModelController<CustomSelectorClient>;
  bookmarkDashboardIds: Set<number>;
  dashboards: Dashboard[];
  loaded: boolean;
  isSavingChanges: boolean;
};

const STOP_SAVING_CHANGES_DEBOUNCE_TIME = 1000;

export class DashboardSelectorController<CustomSelectorClient extends DashboardsModelClient = DashboardsModelClient>
  extends StateController<DashboardSelectorControllerState<CustomSelectorClient>>
  implements Subscribable
{
  declare projectId: number;
  declare bookmarkID: number;
  declare client: CustomSelectorClient;
  declare workspaceId: number;
  declare i18n: LocalizationStore;
  declare dashboardStore: Store<DashboardsMap>;
  declare _loadDashboardsPromises: QueryAllDashboardType;
  declare _loadBookmarkDashboardsPromise: Promise<number[]>;

  constructor({
    projectId,
    bookmarkID,
    dashboardsClient,
    workspaceId,
    i18n,
    dashboardStore = null,
  }: {
    projectId?: number | string;
    bookmarkID?: number;
    dashboardsClient?: CustomSelectorClient;
    workspaceId?: number | string;
    i18n: LocalizationStore;
    dashboardStore?: Store<DashboardsMap>;
  }) {
    super({});
    this.projectId = typeof projectId === `string` ? parseInt(projectId) : projectId;
    this.bookmarkID = bookmarkID;
    this.client = dashboardsClient;
    this.workspaceId = typeof workspaceId === `string` ? parseInt(workspaceId) : workspaceId;

    this.i18n = i18n;
    // note: will be null if this is a public URL, stores don't work in unauthenticated environments
    this.dashboardStore = dashboardStore;
  }
  subscribe(callback: () => void): this {
    this.subscribeUpdates(callback);
    return this;
  }
  unsubscribe(callback: () => void): this {
    this.unsubscribeUpdates(callback);
    return this;
  }

  get defaultState() {
    return {
      activeDashboardController: null,
      bookmarkDashboardIds: new Set<number>(),
      dashboards: [],
      loaded: false,
      isSavingChanges: false,
    };
  }

  _updateCachedDashboard(id, results) {
    const dashboardInList = this.state.dashboards.find((d) => d.id === id);
    if (dashboardInList) {
      Object.assign(dashboardInList, results);
      delete dashboardInList[`cards`]; // don't keep cards in list item
      this._update({dashboards: this.state.dashboards});
    } else {
      this._update({dashboards: [...this.state.dashboards, {...results, id}]});
    }
  }

  getDashboards() {
    return Array.isArray(this.state.dashboards) ? this.state.dashboards : [];
  }

  bookmarkContainsDashboardId(dashboardID) {
    return this.state.bookmarkDashboardIds.has(dashboardID);
  }

  getLoaded() {
    return this.state.loaded;
  }

  // redundant method that is more consistent with out app verbiage
  isLoading() {
    return !this.state.loaded;
  }

  getActiveDashboardController() {
    return this.state.activeDashboardController;
  }

  setBookmarkID(bookmarkID) {
    this.bookmarkID = bookmarkID;
    this._update({loaded: false});
  }

  getIsSavingChanges() {
    return this.state.isSavingChanges;
  }

  setIsSavingChanges(isSavingChanges) {
    this._update({isSavingChanges});
  }

  createDashboardModelController(dashboardID) {
    const dashboardInList = this.state.dashboards.find((d) => d.id === dashboardID);
    return new DashboardModelController({
      model: dashboardInList || {id: dashboardID},
      selectorController: this,
      dashboardsClient: this.client,
      dashboardStore: this.dashboardStore,
      i18n: this.i18n,
    });
  }

  async loadDashboards() {
    this._update({loaded: false});
    if (this.bookmarkID) {
      // prevent redundant network requests
      if (!this._loadDashboardsPromises || !this._loadBookmarkDashboardsPromise) {
        this._loadDashboardsPromises = this.client.queryAll(
          {projectId: this.projectId, workspaceId: this.workspaceId},
          {clearCache: true},
        );
        this._loadBookmarkDashboardsPromise = this.client.queryBookmarkDashboardIds(
          {projectId: this.projectId, bookmarkID: this.bookmarkID, workspaceId: this.workspaceId},
          {clearCache: true},
        );
      }
      // load dashboards containing specified bookmark
      try {
        // note: we store promises separately then "await Promise.all([])" here because
        // array-deconstructing a stored Promise.all([]) promise directly appears to fail when transpiled by babel
        const [dashboards, bookmarkDashboardIds] = await Promise.all([
          this._loadDashboardsPromises.network,
          this._loadBookmarkDashboardsPromise,
        ]);
        this._update({
          dashboards,
          bookmarkDashboardIds: new Set(bookmarkDashboardIds),
          loaded: true,
        });
      } finally {
        this._loadDashboardsPromises = null;
        this._loadBookmarkDashboardsPromise = null;
      }
    } else {
      // prevent redundant network requests
      if (!this._loadDashboardsPromises) {
        this._loadDashboardsPromises = this.client.queryAll(
          {projectId: this.projectId, workspaceId: this.workspaceId},
          {clearCache: true},
        );
      }
      Object.values(this._loadDashboardsPromises).forEach((promise) =>
        promise.then((dashboards) => {
          dashboards && this._update({dashboards, loaded: true});
        }),
      );

      // fallback to network if cached value doesn't exist
      (await this._loadDashboardsPromises.cached) || (await this._loadDashboardsPromises.network);
      Promise.all(Object.values(this._loadDashboardsPromises)).finally(() => (this._loadDashboardsPromises = null));
    }
    return this.state.dashboards;
  }

  async createDashboard({title = null, fields = {}}: {title?; fields?: TODO} = {}) {
    this.onSaveChangesStart();

    if (!title && !fields.duplicate) {
      throw new Error(`Both title or duplicate cannot be empty`);
    }

    const results = await this.client.postCreateDashboard({
      projectId: this.projectId,
      fields: fields.duplicate ? fields : {...fields, title},
      workspaceId: this.workspaceId,
    });
    this._update({dashboards: [...this.state.dashboards, results]});
    return results;
  }

  async createRCADashboard({
    rcaSourceId,
    rcaSourceData,
    rcaEventName,
  }: {
    rcaSourceId?: number;
    rcaSourceData?: RCASourceData;
    rcaEventName?: string;
  }) {
    const results = await this.client.postCreateRCADashboard({
      projectId: this.projectId,
      rcaSourceId,
      rcaSourceData,
      rcaEventName,
      workspaceId: this.workspaceId,
    });
    this._update({dashboards: [...this.state.dashboards, results]});
    return results;
  }

  async triggerRCA({
    bookmarkId,
    bookmarkUrlSlug,
    rcaDate,
    changeDescription,
    metricIndex,
  }: {
    bookmarkId?: number;
    bookmarkUrlSlug?: string;
    rcaDate: string;
    changeDescription?: string;
    metricIndex?: number;
  }) {
    const result = await this.client.postTriggerRCA({
      projectId: this.projectId,
      bookmarkId,
      bookmarkUrlSlug,
      rcaDate,
      changeDescription,
      metricIndex,
    });
    return result;
  }

  getRCAStatus({taskId, dashboardId, offset = 0}: {taskId?: string; dashboardId?: number; offset?: number}) {
    return this.client.getRCAStatus({
      projectId: this.projectId,
      taskId,
      dashboardId,
      offset,
    });
  }

  async deleteDashboard(dashboardID) {
    this.onSaveChangesStart();

    if (!dashboardID) {
      throw new Error(`dashboardID must be specified`);
    }

    const {dashboards} = this.state;
    try {
      this._update({dashboards: dashboards.filter((dash) => dash.id !== dashboardID)});

      await this.client.deleteDashboard({
        dashboardID,
        projectId: this.projectId,
        workspaceId: this.workspaceId,
      });
    } catch (error) {
      if (error.name !== `DashboardDoesNotExistError`) {
        this._update({dashboards});
      }
      throw error;
    }
  }

  async finalizeCopilotDraftDashboard(dashboardID: number, savedBy: `copilot` | `user` = `user`) {
    this.onSaveChangesStart();
    if (!dashboardID) {
      throw new Error(`dashboardID must be specified`);
    }
    try {
      const updated = await this.client.finalizeCopilotDraftDashboard({
        dashboardID,
        projectId: this.projectId,
        workspaceId: this.workspaceId,
        savedBy,
      });
      this._updateCachedDashboard(dashboardID, updated);
      return updated;
    } finally {
      this.debouncedStopSavingChanges();
    }
  }

  async undeleteDashboard(dashboardId: number): Promise<void> {
    this.onSaveChangesStart();

    if (!dashboardId) {
      throw new Error(`dashboardId must be specified`);
    }

    const {dashboards} = this.state;
    const undeletedDashboard = await this.client.undeleteDashboard({
      dashboardId,
      projectId: this.projectId,
      workspaceId: this.workspaceId,
    });
    dashboards.push(undeletedDashboard);
    this._update({dashboards});
  }

  async changeActiveDashboard(dashboardID) {
    const activeDashboardController = this.createDashboardModelController(dashboardID);
    this._update({activeDashboardController});
    try {
      await activeDashboardController.loadModel();
    } catch (err) {
      reporter.error(err);
    }
  }

  resetActiveDashboard() {
    this.setBookmarkID(null);
    this._update({activeDashboardController: this.defaultState.activeDashboardController});
  }

  onSaveChangesStart() {
    this.debouncedStopSavingChanges.cancel();
    this.setIsSavingChanges(true);
  }

  // We use the lodash debounce method to to prevent the `Auto Saving...` flashing.
  // We display the message for at least 1 second
  debouncedStopSavingChanges = debounce(() => {
    this.setIsSavingChanges(false);
  }, STOP_SAVING_CHANGES_DEBOUNCE_TIME);
}
